import React, { useCallback, useState } from 'react';
import { gql, useMutation, useSuspenseQuery } from '@apollo/client';
import { MdAdd, MdLink, MdPerson, MdStore } from 'react-icons/md';
import { toast } from 'react-toastify';
import { withScreen } from '../Screen';
import Button from '../common/Button';
import { AddShopModal } from './AddShopModal';
import { api } from '../../utils/api';
import { useRequestManager } from '../common/RequestManager';

const GET_SHOPS_PAGE_DATA = gql`
  query ShopsPage_GetData {
    me {
      id
      buyerAliexpressCredentials
    }
    shops {
      id
      shopType
      shopKey
      title
      credentials
      orgId
      userId
    }
  }
`;

const PUSH_EBAY_PAYMENT_AND_RETURN_POLICIES = gql`
  mutation ShopsPage_PushEbayPaymentAndReturnPolicies($shopId: ID!, $lang: String!) {
    pushEbayPaymentAndReturnPolicies(shopId: $shopId, lang: $lang) {
      paymentPolicyId
      returnPolicyId
      fulfillmentPolicyId
    }
  }
`;

type ShopsPageData = {
  me: {
    id: string;
    buyerAliexpressCredentials: Record<string, unknown> | null;
  } | null;
  shops: Array<{
    id: string;
    shopType: string;
    shopKey: string;
    title: string;
    credentials: Record<string, unknown> | null;
    orgId: string;
    userId: string;
  }>;
};

function ShopsPage() {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [connectingShopId, setConnectingShopId] = useState<string | null>(null);
  const [connectingBuyer, setConnectingBuyer] = useState(false);
  const [gettingUserShopId, setGettingUserShopId] = useState<string | null>(null);
  const [pushingPoliciesShopId, setPushingPoliciesShopId] = useState<string | null>(null);
  const { trackRequest } = useRequestManager();
  const { data, refetch } = useSuspenseQuery<ShopsPageData>(GET_SHOPS_PAGE_DATA, {
    fetchPolicy: 'cache-and-network',
  });
  const [pushEbayPaymentAndReturnPolicies] = useMutation(PUSH_EBAY_PAYMENT_AND_RETURN_POLICIES);

  const shops = data.shops ?? [];
  const buyerCredentials = data.me?.buyerAliexpressCredentials;

  const handleConnectShop = useCallback(async (shopId: string, shopType: string) => {
    if (shopType !== 'ebay' && shopType !== 'aliexpress') {
      toast.info('Connection flow is not yet available for this shop type');
      return;
    }

    setConnectingShopId(shopId);

    const connectUrlPath = shopType === 'ebay'
      ? '/ebay/get-connect-url?lang=FR'
      : '/aliexpress/get-connect-url';

    try {
      await trackRequest(
        (async () => {
          const response = await api.get(connectUrlPath);
          const responseData = await response.json();

          if (!responseData.connectUrl) {
            throw new Error('No connect URL returned');
          }

          window.location.href = responseData.connectUrl;
        })(),
        {
          title: shopType === 'ebay' ? 'Connecting eBay shop' : 'Connecting Aliexpress shop',
          isBlocking: true,
        },
      );
    } catch (error: any) {
      toast.error(error.message || `Failed to start ${shopType} connection`);
      setConnectingShopId(null);
    }
  }, [trackRequest]);

  const handleConnectBuyer = useCallback(async () => {
    setConnectingBuyer(true);

    try {
      await trackRequest(
        (async () => {
          const response = await api.get('/aliexpress/get-buyer-connect-url');
          const responseData = await response.json();

          if (!responseData.connectUrl) {
            throw new Error('No connect URL returned');
          }

          window.location.href = responseData.connectUrl;
        })(),
        {
          title: 'Connecting Aliexpress buyer account',
          isBlocking: true,
        },
      );
    } catch (error: any) {
      toast.error(error.message || 'Failed to start Aliexpress buyer connection');
      setConnectingBuyer(false);
    }
  }, [trackRequest]);

  const handleSeeUser = useCallback(async (shopId: string) => {
    setGettingUserShopId(shopId);

    try {
      const response = await api.get(`/ebay/user?shop_id=${encodeURIComponent(shopId)}&lang=FR`);
      const user = await response.json();
      console.log(user);
    } catch (error) {
      console.error(error);
    } finally {
      setGettingUserShopId(null);
    }
  }, []);

  const handlePushPaymentAndReturnPolicies = useCallback(async (shopId: string) => {
    setPushingPoliciesShopId(shopId);

    try {
      await trackRequest(
        pushEbayPaymentAndReturnPolicies({
          variables: { shopId, lang: 'FR' },
        }),
        {
          title: 'Pushing eBay shop policies',
          isBlocking: true,
        },
      );
      toast.success('Shop policies pushed successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to push payment and return policies');
    } finally {
      setPushingPoliciesShopId(null);
    }
  }, [pushEbayPaymentAndReturnPolicies, trackRequest]);

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <MdStore className="text-3xl text-gray-700" />
            <h1 className="text-2xl font-bold text-gray-900">Shops ({shops.length})</h1>
          </div>

          <Button preset="primary" onClick={() => setIsAddModalOpen(true)}>
            <MdAdd size={20} />
            Connect Shop
          </Button>
        </div>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
          <div className="px-6 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <MdPerson className="text-2xl text-gray-700 shrink-0" />
              <div className="min-w-0">
                <h2 className="text-sm font-medium text-gray-900">Aliexpress Buyer Account</h2>
                <p className="text-sm text-gray-500 truncate">
                  Personal buyer credentials used for product sync, freight queries, and order fulfillment
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {buyerCredentials ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Configured
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  None
                </span>
              )}
              <Button
                preset="white"
                size="sm"
                onClick={handleConnectBuyer}
                disabled={connectingBuyer}
              >
                <MdLink size={16} />
                {connectingBuyer ? 'Connecting...' : 'Connect'}
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Key
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Credentials
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {shops.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                      No shops found. Add your first shop to get started.
                    </td>
                  </tr>
                ) : (
                  shops.map((shop) => (
                    <tr key={shop.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {shop.title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {shop.shopType}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {shop.shopKey}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {shop.credentials ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Configured
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            None
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <div className="flex justify-end gap-2">
                          {shop.shopType === 'ebay' && shop.credentials && (
                            <>
                              <Button
                                preset="white"
                                size="sm"
                                onClick={() => handleSeeUser(shop.id)}
                                disabled={gettingUserShopId === shop.id}
                              >
                                {gettingUserShopId === shop.id ? 'Loading...' : 'See user'}
                              </Button>
                              <Button
                                preset="white"
                                size="sm"
                                onClick={() => handlePushPaymentAndReturnPolicies(shop.id)}
                                disabled={pushingPoliciesShopId === shop.id}
                              >
                                {pushingPoliciesShopId === shop.id
                                  ? 'Pushing...'
                                  : 'Push Payment & Return policy'}
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AddShopModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => refetch()}
      />
    </>
  );
}

export default withScreen(ShopsPage);
